import { BookingServiceClient } from 'fitbeat.api-clients';
import { HomeWorkoutSequence } from 'fitbeat.gym-core';
import { Instructor as singleInstructorType } from 'fitbeat.models';
import React, { useState } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, LayoutChangeEvent, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FastImage from 'react-native-fast-image';
import Icon from 'react-native-vector-icons/SimpleLineIcons';
import { connect } from 'react-redux';
import { IAppStore } from '../reducer';
import { orientationSelector } from '../selectors/homeWorkoutSelectors';
import { Button } from '../../components';
import { getApiConfig } from '../../config/index';
import Theme from '../../theme';
import { CheckBox } from '../../ui';
import { UserDataConsumer } from '../../utils';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { defaultScale } from '../../utils/scaling';
import {
  changeDefaultInstructor,
  closeInstructorAndDiscardChanges,
  saveAndCloseInstructorScreen,
  setWorkoutInstructor,
} from '../actions';
import { Orientation } from '../reducer';
import { ExpoFileSystem } from '../utils';
import { WorkoutAssetHelper } from '../utils/WorkoutAssetHelper';
import { DeviceSetupSubScreenHeader } from './DeviceSetup/DeviceSetupSubScreenHeader';

interface IProps {
  selectedInstructor: singleInstructorType | null;
  defaultInstructor: string;
  orientation: Orientation;
  instructorList: singleInstructorType[];
  user: any;
  homeWorkoutSession: HomeWorkoutSequence | null;
  saveAndCloseInstructorScreen(): void;
  changeSelectedInstructor(workoutId: string, selectedInstructor: singleInstructorType): void;
  changeDefaultInstructor(userId: string): void;
  closeInstructorAndDiscardChanges(): void;
  t(key: string, value?: any): string;
}

const Instructor = ({
  t,
  saveAndCloseInstructorScreen,
  changeSelectedInstructor,
  changeDefaultInstructor,
  closeInstructorAndDiscardChanges,
  user,
  orientation,
  instructorList,
  defaultInstructor,
  selectedInstructor,
  homeWorkoutSession,
}: IProps) => {
  const isPortrait = orientation === 'PORTRAIT';

  let selectedInstructorChanged = false;
  if (selectedInstructor && homeWorkoutSession) {
    selectedInstructorChanged = selectedInstructor.id !== homeWorkoutSession.workoutSettings.homeWorkoutInstructor;
  }
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const defaultInstructorChanged = defaultInstructor && defaultInstructor !== user.userProfile.homeWorkoutInstructor;
  const instructorSettingsChanged = selectedInstructorChanged || defaultInstructorChanged;

  const instructorListDisplay = () => {
    if (!instructorList || instructorList.length === 0) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={true}
          />
        </View>
      );
    }

    return instructorList.map((item: any) => {
      const isCurrentInstructor = selectedInstructor && item.name === selectedInstructor.name;
      if (item.isActive) {
        if (containerWidth <= 0 || containerHeight <= 0) {
          return <></>;
        }

        return (
          <TouchableOpacity
            onPress={() => changeSelectedInstructor(homeWorkoutSession ? homeWorkoutSession.workoutId : '', item)}
            >
            <View style={[styles.singleInstructorContainer, isPortrait ? {
              width: (containerWidth / 2) - defaultScale(8)
            } : {
              height: containerHeight - defaultScale(60),
            }, !isCurrentInstructor ? {borderColor: Theme.DARK_CHARCOAL} : {borderColor: Theme.WHITE}]}>
              <View style={styles.instructorImageContainer}>
                {item.avatarUrl ? <FastImage style={styles.instructorImageContainer}
                  resizeMode={'contain'}
                  source={{
                    uri: item.avatarUrl,
                  }} /> :
                  <Icon
                    style={{
                      color: Theme.GREY_MEDIUM,
                    }}
                    size={defaultScale(120)}
                    name={'user'}
                  />}
              </View>
              <View style={[styles.instructorNameContainer, isCurrentInstructor && { backgroundColor: Theme.WHITE }]}>
                <Text style={[styles.instructorName, isCurrentInstructor && { color: Theme.CHARCOAL }]}>
                  {item.name}
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        );
      }
    });
  };
  return (
    <View style={styles.mainContainer}>
      <DeviceSetupSubScreenHeader title={t('changeInstructor')} onBackPress={closeInstructorAndDiscardChanges} />
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        onLayout={(event: LayoutChangeEvent) => {
          setContainerWidth(event.nativeEvent.layout.width);
          setContainerHeight(event.nativeEvent.layout.height);
        }}
      >
        <View style={[styles.subHeader, { paddingVertical: defaultScale(10)}]}>
          <Text style={styles.subHeaderText}>
            {t('todaysInstructor', { instructorName: selectedInstructor ? selectedInstructor.name : 'not set' })}
          </Text>
          <Text style={styles.subHeaderText}>{t('feelFreeToWatch')}</Text>
        </View>
        <ScrollView horizontal={!isPortrait}>
          <View style={styles.instructorListContainer}>
            {instructorListDisplay()}
          </View>
        </ScrollView>
      </View>
      <View style={isPortrait ? styles.portraitDefaultAndAcceptContainer : styles.landScapeDefaultAndAcceptContainer}>
        <TouchableOpacity
          onPress={() => changeDefaultInstructor(user.userProfile.uid)}
          style={styles.checkBoxContainer}>
          <CheckBox
            size={defaultScale(16)}
            isActive={!!(selectedInstructor && selectedInstructor.id === defaultInstructor)}
            onPress={() => changeDefaultInstructor(user.userProfile.uid)}
            halfSelect={false}
            inActiveOverrideStyle={{ backgroundColor: Theme.GREY_DARK, borderColor: Theme.GREY_DARK }}
            disabled={false}
          />
          <Text style={styles.defaultInstructorText}>
            {t('defaultInstructor', { instructorName: selectedInstructor ? selectedInstructor.name : '' })}
          </Text>
        </TouchableOpacity>
        <View>
          <Button
            title={'Accept'}
            disabled={!instructorSettingsChanged}
            onPress={saveAndCloseInstructorScreen}
            containerStyle={{
              paddingHorizontal: defaultScale(45),
              backgroundColor: !instructorSettingsChanged ? Theme.GREY_MEDIUM_LIGHT : Theme.PRIMARY,
            }}
            overrideFontStyles={{ fontFamily: Theme.Custom800, color: Theme.WHITE }}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    backgroundColor: Theme.BLACK,
    justifyContent: 'center',
  },
  mainContainer: {
    flex: 1,
    backgroundColor: Theme.BLACK,
    justifyContent: 'space-between',
  },
  subHeader: {
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  subHeaderText: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.WHITE,
  },
  defaultInstructorText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
  },
  instructorListContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  singleInstructorContainer: {
    margin: defaultScale(4),
    borderWidth: 1,
    borderRadius: defaultScale(3),
    borderColor: Theme.GREY_DARK,
    aspectRatio: 3/4,
  },
  instructorImageContainer: {
    flex: 1,
    resizeMode: 'cover',
  },
  instructorNameContainer: {
    backgroundColor: Theme.GREY_DARK,
    height: '25%',
    borderBottomRightRadius: defaultScale(2),
    borderBottomLeftRadius: defaultScale(2),
    justifyContent: 'center',
    alignItems: 'center',
  },
  instructorName: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.WHITE,
  },
  landScapeDefaultAndAcceptContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: defaultScale(10),
  },
  portraitDefaultAndAcceptContainer: {
    flexDirection: 'column',
    marginHorizontal: defaultScale(8),
  },
  checkBoxContainer: {
    flexDirection: 'row',
    marginVertical: defaultScale(8),
    alignItems:'center',
  }
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => ({
  selectedInstructor: state.homeWorkouts.selectedInstructor,
  orientation: orientationSelector(state),
  defaultInstructor: state.homeWorkouts.defaultInstructor,
  instructorList: state.homeWorkouts.instructorList,
  homeWorkoutSession: state.homeWorkouts.homeWorkoutSession,
});

const mapDispatchToProps = (dispatch: any): Partial<IProps> => {
  const bookingServiceClient = new BookingServiceClient(
    getApiConfig(),
    fetch,
    new AuthTokenBuilder());
  const workoutAssetHelper = new WorkoutAssetHelper(new ExpoFileSystem());
  return {
    closeInstructorAndDiscardChanges: () => dispatch(closeInstructorAndDiscardChanges()),
    saveAndCloseInstructorScreen: () =>
      dispatch(saveAndCloseInstructorScreen(bookingServiceClient, workoutAssetHelper)),
    changeSelectedInstructor: (workoutId: string, selectedInstructor: singleInstructorType) =>
      dispatch(setWorkoutInstructor(workoutId, selectedInstructor, bookingServiceClient)),
    changeDefaultInstructor: (userId: string) => dispatch(changeDefaultInstructor(userId, bookingServiceClient)),
  };
};

const voiceInstructorComponent = UserDataConsumer(Instructor);

export const InstructorSetup = connect(mapStateToProps, mapDispatchToProps)(
  translate(['homeWorkouts'])(voiceInstructorComponent));
